import {
	ResultData,
	ResultStatus,
} from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/ResultView";
import {
	GlobalSearchCategoryResult,
	SearchNodeModel,
	GlobalSearchTermModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import create, { SetState } from "zustand";

type GlobalSearchStoreData = {
	searchTerm: string;
	selectedNode: SearchNodeModel | null;
	resultState: ResultData;
	searchCounter: number;
	recentSearchQueries: readonly GlobalSearchTermModel[];
	setSearchTerm: (value: string) => void;
	setSelectedNode: (value: SearchNodeModel) => void;
	setResultState: (value: ResultData) => void;
	setRecentSearchQueries: (value: readonly GlobalSearchTermModel[]) => void;
	startLoading: () => void;
	appendResult: (value: GlobalSearchCategoryResult) => void;
	finishLoading: (wasSuccessful: boolean) => void;
	triggerSearch: () => void;
};

const getInitialState = (
	set: SetState<GlobalSearchStoreData>
): GlobalSearchStoreData => ({
	searchTerm: "",
	resultState: {
		results: [],
		status: ResultStatus.Success,
	},
	selectedNode: null,
	searchCounter: 0,
	recentSearchQueries: [],
	setResultState: (value: ResultData): void => {
		set({ resultState: value });
	},
	startLoading: (): void => {
		set({
			resultState: {
				status: ResultStatus.Loading,
				results: []
			}
		});
	},
	appendResult: (value: GlobalSearchCategoryResult): void => {
		set(x => {
			const { resultState } = x;
			if (resultState.status === ResultStatus.Error) {
				throw new Error("Cannot append to an error");
			}

			return {
				resultState: {
					...resultState,
					results: [...resultState.results, value]
				}
			};
		});
	},
	finishLoading: (wasSuccessful: boolean) => {
		set(x => {
			const { resultState } = x;
			if (resultState.status !== ResultStatus.Loading) {
				throw new Error(`Cannot finish status '${resultState.status}'`);
			}

			const newResultState: ResultData = wasSuccessful
				? {
					...resultState,
					status: ResultStatus.Success
				}
				: {
					status: ResultStatus.Error
				};

			return {
				resultState: newResultState
			};
		});
	},
	setSelectedNode: (node: SearchNodeModel): void => {
		set({ selectedNode: node });
	},
	setSearchTerm: (value: string): void => {
		set({ searchTerm: value });
	},
	triggerSearch: (): void => {
		set((x) => ({ searchCounter: x.searchCounter + 1 }));
	},
	setRecentSearchQueries(value: readonly GlobalSearchTermModel[]): void {
		set({ recentSearchQueries: value });
	},
});

export const useGlobalSearchStore = create<GlobalSearchStoreData>(getInitialState);
